import { Amplify, Auth, Storage } from 'aws-amplify';
import { MutableRefObject } from 'react';

const amplifyConfigForDealershipUserPool = {
  Auth: {
    mandatorySignIn: true,
    region: process.env.REACT_APP_COGNITO_REGION,
    userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
    identityPoolId: process.env.REACT_APP_COGNITO_IDENTITY_POOL_ID,
    userPoolWebClientId: process.env.REACT_APP_COGNITO_APP_CLIENT_ID,
    ...(process.env.REACT_APP_COGNITO_ENDPOINT && {
      endpoint: process.env.REACT_APP_COGNITO_ENDPOINT,
    }),
    authenticationFlowType: 'CUSTOM_AUTH',
  },
  Storage: {
    bucket: process.env.REACT_APP_S3_BUCKET_NAME,
    region: process.env.REACT_APP_COGNITO_REGION,
    identityPoolId: process.env.REACT_APP_COGNITO_IDENTITY_POOL_ID,
  },
};

export const amplifyConfigForConsumerUserPool = {
  Auth: {
    mandatorySignIn: true,
    region: process.env.REACT_APP_CONSUMER_COGNITO_REGION,
    userPoolId: process.env.REACT_APP_CONSUMER_COGNITO_USER_POOL_ID,
    identityPoolId: process.env.REACT_APP_CONSUMER_COGNITO_IDENTITY_POOL_ID,
    userPoolWebClientId: process.env.REACT_APP_CONSUMER_COGNITO_APP_CLIENT_ID,
    ...(process.env.REACT_APP_COGNITO_ENDPOINT && {
      endpoint: process.env.REACT_APP_COGNITO_ENDPOINT,
    }),
    authenticationFlowType: 'CUSTOM_AUTH',
  },
  Storage: {
    bucket: process.env.REACT_APP_S3_BUCKET_NAME,
    region: process.env.REACT_APP_CONSUMER_COGNITO_REGION,
    identityPoolId: process.env.REACT_APP_CONSUMER_COGNITO_IDENTITY_POOL_ID,
  },
};

// Function to dynamically switch user pool configuration
export const configureConsumerAmplify = () => {
  Auth.configure(amplifyConfigForConsumerUserPool);
};

export function configureDealershipAmplify() {
  Amplify.configure(amplifyConfigForDealershipUserPool);
}

export async function verifyUserEmailIsLoggedIn(email: string) {
  const currentUser = await Auth.currentAuthenticatedUser();
  return currentUser.username === email;
}

function generateName(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export interface UploadFileConfig {
  bucket?: string;
  url?: string;
  path?: string;
}

export interface UploadProgress {
  loaded: number;
  total: number;
}

export async function uploadFile(
  file: File,
  progressCallback?: (progress: any) => void,
  uploadOperationRef?: MutableRefObject<any>,
  config?: UploadFileConfig,
  isPrivate?: boolean,
): Promise<string> {
  const fileName = `${config?.path || ''}${generateName()}`;
  const bucket = config?.bucket || process.env.REACT_APP_S3_BUCKET_NAME;

  try {
    const existingFile = await Storage.get(fileName, {
      download: true,
      bucket,
      level: isPrivate ? 'private' : 'public',
    });
    if (existingFile) {
      // File already exist so trigger a new name
      return uploadFile(file, progressCallback, uploadOperationRef, config, isPrivate);
    }
  } catch {
    const uploadOperation = Storage.put(fileName, file, {
      progressCallback,
      contentType: file.type,
      bucket,
      level: isPrivate ? 'private' : 'public',
    });
    if (uploadOperationRef) {
      uploadOperationRef.current = uploadOperation;
    }
    const result = await uploadOperation;
    const middlePrefix = await getMiddlePrefix(isPrivate);
    return `${config?.url || process.env.REACT_APP_S3_URI}${middlePrefix}${result.key}`;
    // return `${process.env.REACT_APP_S3_URI}${result.key}`;
  }
  return '';
}

export async function cancelStorage(operation: any): Promise<boolean> {
  return await Storage.cancel(operation, 'my message for cancellation');
}

async function getMiddlePrefix(isPrivate: boolean): Promise<string> {
  if (!isPrivate) return '';
  const cognitoIdentityId = (await Auth.currentUserCredentials()).identityId;
  return cognitoIdentityId + '/';
}

export const getS3UriFromFileUrl = (fileUrl: string, isPrivate: boolean, bucketName?: string): string => {
  const s3Uri = fileUrl.replace(
    isPrivate ? process.env.REACT_APP_S3_PRIVATE_URI : process.env.REACT_APP_S3_URI || '',
    '',
  );
  const privatePart = isPrivate ? 'private' : 'public';
  return `s3://${bucketName || process.env.REACT_APP_S3_BUCKET_NAME}/${privatePart}/${s3Uri}`;
};
